/**
 * Supported languages/locales configuration - single source of truth.
 *
 * Two-tier approach:
 * - SUPPORTED_LOCALES: All locales the GraphQL backend supports (customer.base_locale)
 * - DASHBOARD_LANGUAGES: Languages with dashboard translation files (subset)
 */

// All locales supported by GraphQL backend (customer.base_locale values)
export const SUPPORTED_LOCALES = [
  'de-DE',  // German (default)
  'en-US',  // English
  'ar-AR',  // Arabic
  'bg-BG',  // Bulgarian
  'es-ES',  // Spanish
  'fa-IR',  // Persian/Farsi
  'fr-FR',  // French
  'hi-IN',  // Hindi
  'it-IT',  // Italian
  'pl-PL',  // Polish
  'pt-PT',  // Portuguese
  'ro-RO',  // Romanian
  'ru-RU',  // Russian
  'sq-AL',  // Albanian
  'th-TH',  // Thai
  'tr-TR',  // Turkish
  'uk-UA',  // Ukrainian
  'zh-CN',  // Chinese (Simplified)
] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];
export const DEFAULT_LOCALE: SupportedLocale = 'de-DE';

// Languages with dashboard translation files (may expand over time)
export const DASHBOARD_LANGUAGES = ['de', 'en'] as const;
export type DashboardLanguage = typeof DASHBOARD_LANGUAGES[number];
export const DEFAULT_DASHBOARD_LANGUAGE: DashboardLanguage = 'de';

/**
 * Extract language code from locale (e.g., 'de-DE' → 'de')
 * Falls back to default dashboard language if not supported.
 */
export function getLanguageFromLocale(locale: string): DashboardLanguage {
  const langCode = locale.split('-')[0].toLowerCase();
  if (DASHBOARD_LANGUAGES.includes(langCode as DashboardLanguage)) {
    return langCode as DashboardLanguage;
  }
  return DEFAULT_DASHBOARD_LANGUAGE;
}

/**
 * Check if a locale is supported by the backend
 */
export function isValidLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

// Per-locale translatable fields (#802). Only these string fields carry a
// translation; every other (structural) field is locale-agnostic and always
// writes to the customer base_locale. A per-locale write (op-level
// `@lang(locale:)`, see UPDATE_ROOM_TRANSLATION_MUTATION) may touch ONLY these.
// Room translations: name + description. Destination translations: name only
// (Destination has no `description` read field — #757/#760).
export const TRANSLATABLE_ROOM_FIELDS = ['name', 'description'] as const;
export const TRANSLATABLE_DESTINATION_FIELDS = ['name'] as const;

/**
 * Translatable fields for a given translation-capable entity type (#802).
 * Returns null for entity types that have no per-locale translation path.
 */
export function translatableFieldsFor(
  entityType: string
): readonly string[] | null {
  if (entityType === 'room') return TRANSLATABLE_ROOM_FIELDS;
  if (entityType === 'destination') return TRANSLATABLE_DESTINATION_FIELDS;
  return null;
}
